<script setup>
import { useForm } from '@inertiajs/vue3';
import FormInput from '@/components/Shared/Form/Input/Index.vue';
import FormEditor from '@/components/Shared/Form/Editor/Index.vue';
import FormButton from '@/components/Shared/Form/Button.vue';
import Card from '@/components/Shared/Card.vue';
import { computed, inject } from 'vue';

defineEmits(['submit']);

const props = defineProps({
  ticketImpacts: {
    type: Array,
    required: true,
  },
  ticketUrgencies: {
    type: Array,
    required: true,
  },
  ticketRequestTypes: {
    type: Array,
    required: true,
  },
  applications: {
    type: Array,
    required: true,
  },
  users: {
    type: Array,
    default: null,
  },
  isPartner: {
    type: Boolean,
    default: true,
  },
  sendToOptions: {
    type: Array,
    default: [],
  },
});

const trans = inject('trans');

const form = useForm({
  request_type: null,
  subject: null,
  message: null,
  application_1: {},
  application_2: {},
  user_ids: [],
  impact: {},
  urgency: {},
  has_workaround_available: false,
  send_to: props.sendToOptions[0],
});

const canSelectCustomers = computed(() => {
  const impact = form.impact?.id;

  // If the impact is All users we cannot select customers.
  return impact !== null && impact !== 0 && impact !== 1 && props.isPartner;
});
</script>

<script>
import DashboardLayout from '@/Layouts/DashboardLayout.vue';

export default {
  layout: DashboardLayout,
};
</script>

<template>
  <div class="m-11">
    <card>
      <template #header>
        <h2 class="text-90 font_bold text-xl uppercase">{{ trans('__JSON__.Create Ticket') }}</h2>
      </template>
      <template #body>
        <form class="my-4 w-full" role="form">
          <div>
            <form-input
              id="ticket-for"
              v-model="form.send_to"
              :error="form.errors.send_to"
              :label-text="trans('__JSON__.Ticket for') + ':'"
              :options="sendToOptions"
              :placeholder="trans('__JSON__.Send ticket for')"
              label="name"
              track-by="id"
              type="select"
            />
          </div>
          <div class="my-2 grid grid-3 gap">
            <form-input
              id="type-question"
              v-model="form.request_type"
              :error="form.errors.request_type"
              :label-text="trans('__JSON__.Type of request')"
              :options="ticketRequestTypes"
              :placeholder="trans('__JSON__.Select type of request')"
              container-class="row-span-2"
              label="name"
              track-by="id"
              type="select"
            />
            <form-input
              id="application-1"
              v-model="form.application_1"
              :error="form.errors.application_1_id"
              :label-text="trans('common.application') + ' 1'"
              :options="applications"
              :placeholder="trans('__JSON__.Select application') + ' 1'"
              label="name"
              track-by="id"
              type="select"
            />
            <form-input
              id="application-2"
              v-model="form.application_2"
              :error="form.errors.application_2_id"
              :label-text="trans('common.application') + ' 2'"
              :options="applications"
              :placeholder="trans('__JSON__.Select application') + ' 2'"
              label="name"
              track-by="id"
              type="select"
            />
          </div>
          <div class="my-2 grid grid-2 gap">
            <form-input
              v-if="isPartner"
              id="impact"
              v-model="form.impact"
              :error="form.errors.impact"
              :label-text="trans('__JSON__.Impact')"
              :options="ticketImpacts"
              :placeholder="trans('__JSON__.Impact')"
              label="name"
              track-by="id"
              type="select"
            />
            <form-input
              id="urgency"
              v-model="form.urgency"
              :error="form.errors.urgency"
              :label-text="trans('__JSON__.Urgency')"
              :options="ticketUrgencies"
              :placeholder="trans('__JSON__.Urgency')"
              label="name"
              track-by="id"
              type="select"
            />
          </div>
          <div v-if="canSelectCustomers" class="my-2">
            <form-input
              id="users"
              v-model="form.user_ids"
              :error="form.errors['user_ids'] || form.errors['users.id']"
              :label-text="trans('__JSON__.Customers affected')"
              :options="users"
              :placeholder="trans('__JSON__.Select customers affected')"
              :type="form.impact?.id === 3 ? 'select' : 'multi-select'"
              label="name"
              track-by="id"
            />
          </div>
          <div>
            <form-input
              v-if="isPartner"
              id="has_workaround_available"
              v-model="form.has_workaround_available"
              :error="form.errors.has_workaround_available"
              :hover-help-text="trans('__JSON__.Is there a workaround available or does this block the complete flow?')"
              :label-text="trans('__JSON__.Workaround available?')"
              container-class="w-full"
              type="checkbox"
            />
            <form-input
              id="subject"
              v-model="form.subject"
              :error="form.errors.subject"
              :label-text="trans('__JSON__.Subject')"
              container-class="w-full"
              type="text"
            />

            <form-editor
              id="message"
              v-model="form.message"
              :error="form.errors.message"
              :label-text="trans('__JSON__.Comment/Question')"
              container-class="w-full my-4"
            />
          </div>
        </form>
      </template>
      <template #footer>
        <div class="flex justify-content-end">
          <form-button
            :processing="form.processing"
            :value="trans('__JSON__.Send')"
            type="button"
            @click="$emit('submit', form)"
          />
        </div>
      </template>
    </card>
  </div>
</template>
